import { useMedplum } from '@medplum/react';
import { formatHumanName, resolveId } from '@medplum/core';
import { Schedule } from '@medplum/fhirtypes';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Alert, Group, Loader, Paper, Select, Table, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ON_CALL_STATS_MONTHS_BACK, ON_CALL_STATS_MONTHS_FORWARD } from '@bariendo/lib/constants';
import {
  fetchOnCallSlotsForMonth,
  fetchPractitionersByIds,
  getOnCallStatsMonths,
  summariseOnCallHours,
  type OnCallPractitionerHours,
} from '@bariendo/lib/on-call';

interface OnCallMonthlyStatsProps {
  schedule: Schedule | undefined;
  timezone: string;
  /** Changes after each write so totals follow edits made in the table below. */
  revision: number;
}

interface MonthTotals {
  rows: OnCallPractitionerHours[];
  namesById: Map<string, string>;
}

const EMPTY_TOTALS: MonthTotals = { rows: [], namesById: new Map() };

/** Shifts are half-hour aligned, so anything finer than a half hour would be noise. */
function formatHours(hours: number): string {
  return `${Math.round(hours * 2) / 2}h`;
}

export function OnCallMonthlyStats({ schedule, timezone, revision }: OnCallMonthlyStatsProps): ReactElement {
  const medplum = useMedplum();

  const months = useMemo(
    () => getOnCallStatsMonths(timezone, ON_CALL_STATS_MONTHS_BACK, ON_CALL_STATS_MONTHS_FORWARD),
    [timezone]
  );
  // Resolved by value rather than by index into `months`, so the default does not silently
  // move if the span constants change.
  const currentMonth = useMemo(() => getOnCallStatsMonths(timezone, 0, 0)[0]?.value ?? '', [timezone]);

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [totals, setTotals] = useState<MonthTotals>(EMPTY_TOTALS);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!schedule) {
        setTotals(EMPTY_TOTALS);
        setFailed(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setFailed(false);

      try {
        const slots = await fetchOnCallSlotsForMonth(medplum, schedule, selectedMonth, timezone);
        const rows = summariseOnCallHours(slots, selectedMonth, timezone);

        // Names come from the referenced practitioners, not the currently eligible roster:
        // for past months anyone who has since changed role or left is absent from that
        // roster, and would otherwise be reported as unknown in exactly the months this
        // panel exists to show.
        const practitioners = await fetchPractitionersByIds(
          medplum,
          rows.map((row) => resolveId(row.practitionerRef)).filter((id): id is string => !!id)
        );

        if (cancelled) {
          return;
        }

        setTotals({
          rows,
          namesById: new Map(
            [...practitioners].map(([id, practitioner]) => [id, formatHumanName(practitioner.name?.[0]) || id])
          ),
        });
      } catch {
        if (!cancelled) {
          // Deliberately distinct from an empty month. Saying "nobody is assigned" when the
          // query failed invites someone to conclude a month is uncovered when it is not.
          setTotals(EMPTY_TOTALS);
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [medplum, schedule, selectedMonth, timezone, revision]);

  return (
    <Paper withBorder p="md" mb="md">
      <Group justify="space-between" align="center" mb="sm">
        <Text fw={600}>Hours on call</Text>
        <Select
          data={months}
          value={selectedMonth}
          onChange={(value) => setSelectedMonth(value ?? currentMonth)}
          allowDeselect={false}
          aria-label="Month"
          w={200}
        />
      </Group>

      {loading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : failed ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Could not load hours for this month.
        </Alert>
      ) : totals.rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nobody is assigned this month.
        </Text>
      ) : (
        <Table>
          <Table.Tbody>
            {totals.rows.map(({ practitionerRef, hours }) => {
              const id = resolveId(practitionerRef);

              return (
                <Table.Tr key={practitionerRef.reference}>
                  <Table.Td>{(id && totals.namesById.get(id)) ?? practitionerRef.display ?? id ?? 'Unknown'}</Table.Td>
                  <Table.Td w={100} ta="right">
                    {formatHours(hours)}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}
